import Keycloak from "keycloak-js";
import type {
  KeycloakInitOptions,
  KeycloakLoginOptions,
  KeycloakLogoutOptions,
} from "keycloak-js";
import axios from "axios";
import Cookies from "js-cookie";
import pubsub from "@viewlift/pubsub";
import {
  fetchUserData,
  getDeviceId,
  getToken,
  handleSeviceTypes,
  handleUserLogInState,
  handleUserTransactions,
  setToken,
} from "../../../helpers";
import { getStorageItem, setStorageItem } from '../../../helpers/storageManager'

import { publishRedirect } from "../../Events/utils.events";

let keycloakInstance: Keycloak | null = null;

interface KeycloakConfig {
  isEnabled: boolean;
  authServerUrl: string;
  realm: string;
  clientId: string;
  callbackUrl?: string;
}

const DEFAULT_INIT_OPTIONS: KeycloakInitOptions = {
  checkLoginIframe: false,
  pkceMethod: false,
  responseMode: "query",
  scope: "openid email profile",
};

const getKeycloakConfig = (): KeycloakConfig | null => {
  if (typeof window === "undefined") return null;
  return (window as any)?.app_data?.appcmsMain?.ssoConfig?.keycloak || null;
};

export const isKeycloakEnabled = (): boolean =>
  getKeycloakConfig()?.isEnabled === true;

const getRedirectUri = (): string => {
  const config = getKeycloakConfig();
  if (typeof window === "undefined") return "";

  return config?.callbackUrl ?? window.location.href;
};

export const getKeycloakInstance = async (): Promise<Keycloak | null> => {
  if (keycloakInstance) return keycloakInstance;

  const config = getKeycloakConfig();

  if (!config?.authServerUrl || !config?.realm || !config?.clientId) {
    console.error(
      "Keycloak sso configuration is incomplete: authServerUrl, realm, and clientId are required",
    );
    return null;
  }

  keycloakInstance = new Keycloak({
    url: config.authServerUrl,
    realm: config.realm,
    clientId: config.clientId,
  });

  return keycloakInstance;
};

export const initKeycloak = async (
  options: KeycloakInitOptions = {},
): Promise<Keycloak | null> => {
  if (typeof window === "undefined" || !isKeycloakEnabled()) {
    return null;
  }

  try {
    const keycloak = await getKeycloakInstance();

    if (!keycloak) {
      return null;
    }

    // keycloak-js has already been initialized
    if (keycloak.didInitialize) {
      return keycloak;
    }

    await keycloak.init({
      ...DEFAULT_INIT_OPTIONS,
      ...options,
    });

    return keycloak;
  } catch (error: any) {
    if (
      error?.message?.includes("already initialized") ||
      error?.message?.includes("initialized once")
    ) {
      return keycloakInstance;
    }
    console.error("Failed to initialize Keycloak adapter:", error);
    return null;
  }
};

export const initKeycloakSignIn = async (
  options: KeycloakLoginOptions = {},
): Promise<void> => {
  if (isUserKeycloakAuthenticated()) {
    const isRedirected = getStorageItem("isRedirected");
    const redirectPath = isRedirected?.path || "/";
    publishRedirect(redirectPath);
    return;
  }
  if (hasVlCodeQueryParam() || !isKeycloakEnabled()) return;

  const keycloak = await initKeycloak();

  if (!keycloak) {
    return;
  }

  // save current location to isRedirected storage to redirect back after login
  const isRedirected = getStorageItem("isRedirected");
  const redirectPath = location?.pathname !== "/authentication"
      ? location?.pathname : isRedirected?.path || "/";

  setStorageItem("isRedirected", {
    ...(isRedirected ?? {}),
    path: redirectPath,
    redirect: true,
  });

  // Replace the current authentication URL in browser history to prevent navigating back to the /authentication callback page.
  window.history.replaceState({}, document.title, "/");

  const loginOptions: KeycloakLoginOptions = {
    redirectUri: getRedirectUri(),
    ...options,
  };

  pubsub.publish("apploading", true);
  try {
    await keycloak.login(loginOptions);
  } catch (error) {
    pubsub.publish("apploading", false);
    console.error("Keycloak Login Error:", error);
  }
};

const exchangeKeycloakVlCode = async (vlCode: string): Promise<void> => {
  try {
    const apiBaseUrl = (window as any)?.app_data?.appcmsMain?.apiConfig?.apiBaseUrl;
    const xApiKey = (window as any)?.xApiKey;
    const authorizationToken = await getToken();
    const endpoint = `${apiBaseUrl}/v4/identity/keycloak/viewlift/token`;

    const headers = {
      "Content-Type": "application/json",
      "x-api-key": xApiKey,
      Authorization: authorizationToken || "",
    };

    const params = {
      deviceId: getDeviceId(),
      deviceName: "web_browser",
      platform: "web",
    };

    const body = {
      vlCode,
    };

    const response = await axios.post(endpoint, body, {
      params,
      headers,
    });

    const authToken = response?.data?.authorizationToken;
    const refreshToken = response?.data?.refreshToken;
    const keycloakRefreshToken = response?.data?.keycloakRefreshToken;

    if (authToken && refreshToken) {
      setToken({
        authorizationToken: authToken,
        refreshToken,
        keycloakRefreshToken,
      });

      const userData = await fetchUserData();
      await handleUserLogInState();
      handleSeviceTypes().TVOD && await handleUserTransactions();
      await syncSubscriptionBeforeRedirect(apiBaseUrl, xApiKey, (userData as any)?.userId);

      pubsub.publish("isAuthenticated", {
        ...userData,
        provider: "KEYCLOAK",
        keycloakToken: vlCode,
      });

      const isRedirected = getStorageItem("isRedirected");
      const redirectPath = isRedirected?.path || "/";

      if (redirectPath) {
        setStorageItem("isRedirected", {
          ...isRedirected,
          redirect: false,
        });
        clearKeycloakCallbackParams();
        window.location.assign(redirectPath);
        return;
      }
    }
  } catch (error) {
    console.error("Keycloak Token Exchange Error:", error);
    throw error;
  } finally {
    pubsub.publish("apploading", false);
  }
};

export const handleKeycloakCallback = async (): Promise<boolean> => {
  
  if (typeof window === "undefined" || !isKeycloakEnabled() || !hasVlCodeQueryParam()) return false;
  const vlCode = getParam("vlCode");

  if (!vlCode) {
    console.error("Keycloak callback processing error: Missing vlCode parameter");
    return false;
  }

  try {
    pubsub.publish("apploading", true);
    await exchangeKeycloakVlCode(vlCode);
    return true;
  } catch (err) {
    console.error("Keycloak callback processing error:", err);
    pubsub.publish("apploading", false);
    clearKeycloakCallbackParams();
    return true;
  }
};

export const signOutKeycloakSession = async (): Promise<void> => {
  if (!isKeycloakEnabled()) return;
  const apiBaseUrl = (window as any)?.app_data?.appcmsMain?.apiConfig
    ?.apiBaseUrl;
  const keycloakRefreshToken = getKeycloakRefreshToken();

  if (!apiBaseUrl || !keycloakRefreshToken) {
    return;
  }

  const authorizationToken = await getToken();
  const headers = {
    "Content-Type": "application/json",
    Authorization: authorizationToken || "",
    "x-api-key": (window as any)?.xApiKey,
  };
  const payload = {
    deviceId: getDeviceId(),
    keycloakRefreshToken,
  };

  await axios.post(`${apiBaseUrl}/v4/identity/signout`, payload, {
    headers,
  });
};

/**
 * Fetches and persists the user's subscription data into AuthenticationStore
 * synchronously before a Keycloak redirect, so that detail pages (VideoDetail02,
 * ShowDetailModule02, etc.) see the correct isSubscribed state on their very
 * first mount — without needing their own "update-user-state" subscription.
 */
const syncSubscriptionBeforeRedirect = async (
  apiBaseUrl: string,
  xApiKey: string,
  userId: string,
): Promise<void> => {
  try {
    const token = await getToken();
    if (!userId || !token) return;
    const subRes = await axios.get(`${apiBaseUrl}/v3/subscription/user`, {
      params: { site: (window as any)?.app_data?.site?.siteInternalName, userId },
      headers: { Authorization: token, 'x-api-key': xApiKey },
    });
    const subscriptionStatus = subRes?.data?.subscriptionInfo?.subscriptionStatus;
    // TODO: replace with imported helper once checkSubscription is merged into prod.
    const isSubscribed = ((subscriptionStatus === 'COMPLETED') || (subscriptionStatus === "DEFERRED_CANCELLATION")) ? true : false;
    const store = getStorageItem('AuthenticationStore');
    setStorageItem('AuthenticationStore', {
      user: {
        ...store?.user,
        subscription: { ...subRes.data, subscriptionStatus },
        isSubscribed,
      },
    });
  } catch (subError) {
    console.warn('Keycloak: could not pre-fetch subscription data before redirect', subError);
  }
};

export const getKeycloakRefreshToken = (): string | null => {
  const tokenCookie = Cookies.get("token");
  if (!tokenCookie) return null;

  try {
    const parsed = JSON.parse(tokenCookie);
    return parsed?.keycloakRefreshToken || null;
  } catch (error) {
    console.error("Failed to parse Keycloak refresh token from cookie:", error);
    return null;
  }
};

export const isUserKeycloakAuthenticated = (): boolean => {
  if (!isKeycloakEnabled()) return false;
  const userData = getStorageItem('AuthenticationStore');
  return  Boolean(userData?.user?.provider === 'keycloak');
}

export const isKeycloakAuthFlow = (): boolean => {
  if (typeof window === "undefined") return false;
  return isKeycloakEnabled() && Boolean(getParam("vlCode"));
};

const getParam = (key: string) =>
  new URLSearchParams(window.location.search).get(key) ||
  new URLSearchParams(window.location.hash.slice(1)).get(key);

const hasVlCodeQueryParam = (): boolean => {
  if (typeof window === "undefined") return false;
  return Boolean(getParam("vlCode"));
};

const clearKeycloakCallbackParams = (): void => {
  if (typeof window === "undefined") return;

  const url = new URL(window.location.href);

  if (url.searchParams.has("vlCode")) {
    url.searchParams.delete("vlCode");
  }

  if (url.searchParams.has("site")) {
    url.searchParams.delete("site");
  }

  if (url.searchParams.has("state")) {
    url.searchParams.delete("state");
  }

  if (url.hash.includes("site")) {
    url.searchParams.delete("site");
  }

  const nextUrl = `${url.pathname}${url.search}${url.hash}` || "/";
  window.history.replaceState({}, document.title, nextUrl);
};